// When importing this file from a .js file, always include the .ts extension in the import statement.
// Example: import { useDocuments } from '../hooks/useDocuments.ts';
// This prevents module resolution errors in mixed JS/TS codebases.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

export interface Document {
  id: number;
  title: string;
  description?: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  category: string;
  is_public: boolean;
  permissions?: number[];
  created_at: string;
  updated_at: string;
  uploaded_by: number;
  uploadedBy: {
    id: number;
    name: string;
  };
  formatted_file_size?: string;
  file_url?: string;
}

export interface DocumentStats {
  total_documents: number;
  total_size: number;
  categories: Record<string, number>;
  recent_uploads: Document[];
}

export interface DocumentFilters {
  category?: string;
  search?: string;
}

export interface UploadDocumentData {
  title: string;
  description?: string;
  category: string;
  is_public: boolean;
  permissions?: number[];
  file: File;
}

export interface UpdateDocumentData {
  title: string;
  description?: string;
  category: string;
  is_public: boolean;
  permissions?: number[];
}

// Get documents with filters
export const useDocuments = (filters: DocumentFilters = {}) => {
  return useQuery({
    queryKey: ['documents', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.category && filters.category !== 'all') {
        params.append('category', filters.category);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }

      const response = await api.get(`/documents?${params.toString()}`);
      return response.data;
    },
  });
};

// Get document statistics
export const useDocumentStats = () => {
  return useQuery({
    queryKey: ['documents', 'stats'],
    queryFn: async () => {
      const response = await api.get('/documents/stats');
      return response.data.data as DocumentStats;
    },
  });
};

// Get single document
export const useDocument = (id: number) => {
  return useQuery({
    queryKey: ['documents', id],
    queryFn: async () => {
      const response = await api.get(`/documents/${id}`);
      return response.data.data as Document;
    },
    enabled: !!id,
  });
};

// Upload document
export const useUploadDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UploadDocumentData) => {
      const formData = new FormData();
      formData.append('title', data.title);
      if (data.description) {
        formData.append('description', data.description);
      }
      formData.append('category', data.category);
      formData.append('is_public', data.is_public ? '1' : '0');
      if (data.permissions) {
        data.permissions.forEach(id => {
          formData.append('permissions[]', id.toString());
        });
      }
      formData.append('file', data.file);

      const response = await api.post('/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents', 'stats'] });
    },
  });
};

// Update document
export const useUpdateDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateDocumentData & { file?: File } }) => {
      if (data.file) {
        const formData = new FormData();
        formData.append('title', data.title);
        if (data.description) formData.append('description', data.description);
        formData.append('category', data.category);
        formData.append('is_public', data.is_public ? '1' : '0');
        if (data.permissions) {
          data.permissions.forEach(id => {
            formData.append('permissions[]', id.toString());
          });
        }
        formData.append('file', data.file);
        const response = await api.post(`/documents/${id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
      } else {
        const response = await api.put(`/documents/${id}`, {
          title: data.title,
          description: data.description,
          category: data.category,
          is_public: data.is_public,
          permissions: data.permissions,
        });
        return response.data;
      }
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents', id] });
      queryClient.invalidateQueries({ queryKey: ['documents', 'stats'] });
    },
  });
};

// Delete document
export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/documents/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents', 'stats'] });
    },
  });
};

// Download document
export const useDownloadDocument = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.get(`/documents/${id}/download`, {
        responseType: 'blob',
      });
      return response.data;
    },
  });
};

// Document categories
export const DOCUMENT_CATEGORIES = {
  general: 'General',
  financial: 'Financial',
  legal: 'Legal',
  meeting_minutes: 'Meeting Minutes',
  contracts: 'Contracts',
  reports: 'Reports',
  policies: 'Policies',
  forms: 'Forms',
  other: 'Other',
};

// File type icons
export const getFileIcon = (fileType: string): string => {
  const type = fileType.toLowerCase();
  
  if (type === 'pdf') return '📄';
  if (['doc', 'docx'].includes(type)) return '📝';
  if (['xls', 'xlsx'].includes(type)) return '📊';
  if (['ppt', 'pptx'].includes(type)) return '📈';
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(type)) return '🖼️';
  if (['zip', 'rar'].includes(type)) return '📦';
  if (type === 'txt') return '📄';
  if (type === 'csv') return '📊';
  
  return '📎';
};

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}; 